import {
  Contact,
  ContactListsResponse,
  contactListsResponseSchema,
  contactSchema,
} from '@contact/types/contact';
import { ContactFormPayload } from '@contact/types/contactForm';
import {
  ContactsUniquenessPayload,
  ContactsUniquenessResponse,
  contactsUniquenessResponseSchema,
  ContactsTopicsResponse,
  contactsTopicsResponseSchema,
  CreateContactPayload,
  CreateContactResponse,
  createContactResponseSchema,
  ExistingContactsPayload,
  ExistingContacts,
  existingContactsResponseSchema,
} from '@contact/types/contacts';
import { axiosApiInstance, validateResponse } from '@lib/api';

import { RequestContactUpdatePayload } from '../types/requestContactUpdate';

import { contactsAPI } from './contactsApi';

export const createContact = async (payload: CreateContactPayload) => {
  const response = await axiosApiInstance.post<CreateContactResponse>(
    contactsAPI.contacts,
    payload,
  );

  return validateResponse(createContactResponseSchema, response).data;
};

export const getContact = async (contactId: number) => {
  const response = await axiosApiInstance.get<Contact>(contactsAPI.contact(contactId));

  return validateResponse(contactSchema, response).data;
};

export const getContactLists = async (contactId: number, page: number) => {
  const response = await axiosApiInstance.get<ContactListsResponse>(
    contactsAPI.contactLists(contactId),
    {
      params: {
        page,
      },
    },
  );

  const { headers } = response;
  const { data } = validateResponse(
    contactListsResponseSchema.omit({ pagination: true }),
    response,
  );

  return {
    ...data,
    pagination: {
      pageItems: Number(headers.pageItems),
      totalCount: Number(headers.totalCount),
      totalPages: Number(headers.totalPages),
      currentPage: Number(headers.currentPage),
    },
  };
};

export const updateContact = async ({ id, contact }: ContactFormPayload) => {
  const response = await axiosApiInstance.patch<Contact>(contactsAPI.contact(id), contact);

  return validateResponse(contactSchema, response).data;
};

export const checkContactsUniqueness = async (payload: ContactsUniquenessPayload) => {
  const response = await axiosApiInstance.post<ContactsUniquenessResponse>(
    contactsAPI.areContactsUnique,
    payload,
  );

  return validateResponse(contactsUniquenessResponseSchema, response).data;
};

export const getContactsTopics = async (search?: string) => {
  const response = await axiosApiInstance.get<ContactsTopicsResponse>(contactsAPI.contactsTopics, {
    params: { q: search ?? '' },
  });

  return validateResponse(contactsTopicsResponseSchema, response).data;
};

export const getExistingContacts = async (payload: ExistingContactsPayload) => {
  if (payload.sourceUids.length === 0) {
    return {};
  }

  const response = await axiosApiInstance.post<ExistingContacts>(
    contactsAPI.existingContacts,
    payload,
  );

  return validateResponse(existingContactsResponseSchema, response).data;
};

export const requestContactUpdate = async (payload: RequestContactUpdatePayload) => {
  return await axiosApiInstance.post(contactsAPI.requestContactUpdate, payload);
};

export const removeContactFromMediaLists = async (contactId: number): Promise<void> => {
  await axiosApiInstance.delete(contactsAPI.removeFromAllLists(contactId));
};
